// Pure logica van de tijdkiezer (TimeField, TimePicker): getypte tijden begrijpen, duur tonen en
// de rasters opbouwen. Geen DOM, zodat alles los te testen is (time.test.ts).

/** Minuten na middernacht van "HH:MM", of null als het geen geldige tijd is. */
export function minutesOfTime(hhmm: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
}

/** "HH:MM" van minuten na middernacht (0..1439). */
export function timeOfMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h < 10 ? '0' : ''}${h}:${m < 10 ? '0' : ''}${m}`;
}

export type ParsedTime = { ok: true; value: string } | { ok: false };

/**
 * Begrijpt wat ouders typen: "1530", "15:30", "15.30", "15,30", "15u30", "15 30", "930" (09:30),
 * "9" (09:00), "9u", "9 uur", "15h". Leeg is geldig (geen tijd). Alles daarbuiten → { ok: false },
 * zodat het veld een rustige hint kan tonen in plaats van stil terug te springen.
 */
export function parseTime(raw: string): ParsedTime {
  const text = raw.trim().toLowerCase();
  if (text === '') return { ok: true, value: '' };
  // Achtervoegsels "u", "uur", "h" (ook met spatie ervoor) mogen weg.
  const clean = text.replace(/\s*(uur|u|h)\s*$/, '');
  let h: number;
  let m: number;
  const split = /^(\d{1,2})\s*(:|\.|,|u|h|\s)\s*(\d{1,2})$/.exec(clean);
  if (split) {
    h = Number(split[1]);
    const sep = split[2];
    const mins = split[3] ?? '';
    // Eén cijfer na een punt, komma, "u", "h" of spatie ("19.5", "19u5", "19 5") is dubbelzinnig:
    // half acht (decimaal), 19:05 ("19 uur 5") of 19:50? Dan liever de rustige hint dan stil een
    // tijd kiezen (review c-fs r1 en r2). Alleen na een dubbele punt is het gewoon: zie hieronder.
    if (mins.length === 1 && sep !== ':') return { ok: false };
    // "15:3" leest als 15:30 (zoals je het uitspreekt); "15:03" blijft 15:03.
    m = mins.length === 1 ? Number(mins) * 10 : Number(mins);
  } else if (/^\d{1,4}$/.test(clean)) {
    if (clean.length <= 2) {
      h = Number(clean);
      m = 0;
    } else {
      h = Number(clean.slice(0, clean.length - 2));
      m = Number(clean.slice(-2));
    }
  } else {
    return { ok: false };
  }
  if (!(h >= 0 && h <= 23 && m >= 0 && m <= 59)) return { ok: false };
  return { ok: true, value: timeOfMinutes(h * 60 + m) };
}

/** Of een tijd binnen min/max valt (beide optioneel, "HH:MM"). */
export function inRange(value: string, min?: string, max?: string): boolean {
  const v = minutesOfTime(value);
  if (v === null) return false;
  const lo = min ? minutesOfTime(min) : null;
  const hi = max ? minutesOfTime(max) : null;
  return (lo === null || v >= lo) && (hi === null || v <= hi);
}

/** Minuten tussen twee tijden (eind − begin), of null als een van beide ongeldig is. */
export function durationBetween(start: string, end: string): number | null {
  const s = minutesOfTime(start);
  const e = minutesOfTime(end);
  return s === null || e === null ? null : e - s;
}

/** Begin + duur als "HH:MM", of null als het over middernacht zou gaan. */
export function addDuration(start: string, minutes: number): string | null {
  const s = minutesOfTime(start);
  if (s === null) return null;
  const total = s + minutes;
  return total >= 0 && total <= 23 * 60 + 59 ? timeOfMinutes(total) : null;
}

export interface DurationLabels {
  /** Met `{count}`: "30 min". */
  minutesShort: string;
  /** Met `{count}`: "1 uur" / "2 uur". */
  hoursShort: { one: string; other: string };
}

function fill(template: string, count: number | string): string {
  return template.replace('{count}', String(count));
}

/** Duur als "30 min", "1 uur", "1½ uur", "2 uur", "1 uur 15 min" (nooit "1,5"). */
export function formatDuration(minutes: number, labels: DurationLabels): string {
  if (minutes < 60) return fill(labels.minutesShort, minutes);
  const h = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (rest === 0) return fill(h === 1 ? labels.hoursShort.one : labels.hoursShort.other, h);
  if (rest === 30) return fill(labels.hoursShort.other, `${h}½`);
  return `${fill(h === 1 ? labels.hoursShort.one : labels.hoursShort.other, h)} ${fill(
    labels.minutesShort,
    rest,
  )}`;
}

/** Standaardduren onder "Tot": een half uur tot twee uur. */
export const DURATIONS: readonly number[] = [30, 45, 60, 90, 120];

/** Uren per rij in het raster (zes breed: 06–11, 12–17, 18–23). */
export const HOUR_COLS = 6;
/** Minuten per rij: eerst de kwartieren (groot), dan de vijftallen (kleiner). */
export const MINUTE_COLS = 4;

/** Uren in het raster: overdag 06–23; de nacht 00–05 alleen als die aan staat. */
export function hourOptions(night: boolean): number[] {
  const out: number[] = [];
  for (let h = night ? 0 : 6; h < 24; h += 1) out.push(h);
  return out;
}

/**
 * Minuten in het raster: 00, 15, 30, 45 (groot), daarna 05, 10, 20, 25, 35, 40, 50, 55. Met een
 * grotere stap (bv. 900 s = kwartier) vallen de vijftallen weg.
 */
export function minuteOptions(stepSeconds?: number): number[] {
  const step = stepSeconds && stepSeconds >= 60 ? Math.round(stepSeconds / 60) : 5;
  const quarters = [0, 15, 30, 45];
  const fives = [5, 10, 20, 25, 35, 40, 50, 55];
  return [...quarters, ...fives].filter((m) => m % step === 0);
}

/** Of de nachturen open moeten bij deze waarde (een tijd vóór 06:00). */
export function isNight(value: string): boolean {
  const v = minutesOfTime(value);
  return v !== null && v < 6 * 60;
}

export type GridKey = 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown' | 'Home' | 'End';

/**
 * Pijltjestoetsen in een raster van `count` knoppen, `cols` breed: links/rechts lopen door naar
 * de vorige/volgende rij, op/neer blijven in dezelfde kolom; aan de rand blijft de focus staan.
 */
export function moveInGrid(index: number, key: GridKey, cols: number, count: number): number {
  let next = index;
  if (key === 'ArrowLeft') next = index - 1;
  else if (key === 'ArrowRight') next = index + 1;
  else if (key === 'ArrowUp') next = index - cols;
  else if (key === 'ArrowDown') next = index + cols;
  else if (key === 'Home') next = 0;
  else if (key === 'End') next = count - 1;
  return next < 0 || next >= count ? index : next;
}

/** Een getal met voorloopnul: 9 → "09". */
export function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
